import axios from "axios";
import { toast } from "react-toastify";
import Config from "../../Config";

const apiRoot = Config.promoApiBase;

const authHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const readEnvelope = (response) => {
  const body = response?.data ?? {};
  if (body?.status_code != null) return body;
  if (body?.data?.status_code != null) return body.data;
  return body;
};

const isSuccess = (envelope, codes) =>
  envelope?.in_error !== true && codes.includes(String(envelope?.status_code ?? ""));

const errorMessage = (error, fallback) => {
  const envelope = error.response ? readEnvelope(error.response) : null;
  const errors = error.response?.data?.errors || error.response?.data?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat()[0];
    if (first) return first;
  }
  return envelope?.reason || error.message || fallback;
};

const appendImageFile = (form, key, file) => {
  if (file instanceof File) {
    form.append(key, file, file.name);
  }
};

const flags = (values) => ({
  is_active: values.active ? 1 : 0,
  featured: values.featured ? 1 : 0,
});

export const isSubcategoryRecord = (item) => {
  if (!item) return false;
  if (item.category_id != null && item.category_id !== "") return true;
  if (item.parent_id != null && item.parent_id !== "") return true;
  if (item.parentId != null && item.parentId !== "") return true;
  const kind = String(item.type || item.categoryType || "").toLowerCase();
  return kind === "subcategory" || kind === "child";
};

const createdId = (envelope) => {
  const created = envelope?.data;
  return created?.id ?? null;
};

export const fetchCategories = async (setCategories, setLoading, setError) => {
  setLoading(true);
  setError(null);

  try {
    const response = await axios.get(`${apiRoot}/admin/categories`, {
      headers: authHeaders(),
    });
    const envelope = readEnvelope(response);
    const list = Array.isArray(envelope) ? envelope : envelope?.data;
    const status = Array.isArray(envelope) ? "000" : envelope?.status_code;
    if (
      !Array.isArray(list) ||
      envelope?.in_error === true ||
      (status != null && !["000", "001"].includes(String(status)))
    ) {
      throw new Error(envelope?.reason || "Failed to fetch categories");
    }
    setCategories(list);
  } catch (error) {
    setError(errorMessage(error, "Failed to fetch categories"));
  } finally {
    setLoading(false);
  }
};

export const fetchCategory = async (categorySlug) => {
  const response = await axios.get(`${apiRoot}/categories/${categorySlug}`);
  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["000"]) || envelope?.in_error) {
    throw new Error(envelope?.reason || "Category not found");
  }
  return envelope.data;
};

export const fetchSubcategories = async (categoryId) => {
  const query = categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : "";
  const response = await axios.get(`${apiRoot}/admin/subcategories${query}`, {
    headers: authHeaders(),
  });
  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["000"])) {
    throw new Error(envelope?.reason || "Failed to fetch subcategories");
  }
  return Array.isArray(envelope.data) ? envelope.data : [];
};

export const fetchSubcategory = async (id) => {
  const response = await axios.get(`${apiRoot}/subcategories/${id}`);
  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["000"]) || envelope?.in_error) {
    throw new Error(envelope?.reason || "Subcategory not found");
  }
  return envelope.data;
};

const createCategory = async (values) => {
  const form = new FormData();
  form.append("name", values.name);
  form.append("type", "Department");
  form.append("is_active", values.active ? "1" : "0");
  form.append("featured", values.featured ? "1" : "0");
  appendImageFile(form, "cover_image", values.cover);
  appendImageFile(form, "thumbnail", values.thumbnail);

  const response = await axios.post(`${apiRoot}/admin/categories`, form, {
    headers: authHeaders(),
  });
  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["001", "000"])) {
    throw new Error(envelope?.reason || "Failed to create category");
  }
  return createdId(envelope);
};

const createSubcategory = async (values) => {
  const form = new FormData();
  form.append("name", values.name);
  form.append("category_id", String(values.categoryId));
  form.append("is_active", values.active ? "1" : "0");
  form.append("featured", values.featured ? "1" : "0");
  appendImageFile(form, "cover_image", values.cover);
  appendImageFile(form, "thumbnail", values.thumbnail);

  const response = await axios.post(`${apiRoot}/admin/subcategories`, form, {
    headers: authHeaders(),
  });
  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["001", "000"])) {
    throw new Error(envelope?.reason || "Failed to create subcategory");
  }
  return createdId(envelope);
};

export const handleCategoryCreate = async (values, { onSave, onClose, onProgress } = {}) => {
  if (!localStorage.getItem("auth_token")) {
    toast.error("Failed to create category");
    return;
  }

  try {
    const isSubcategory = values.placement === "subcategory";
    if (isSubcategory) {
      const fromCards = (values.subcategories || []).filter((item) => item.name.trim());
      const nested = fromCards.length
        ? fromCards
        : values.name?.trim()
          ? [{
              name: values.name,
              active: values.active,
              featured: values.featured,
              cover: values.cover,
              thumbnail: values.thumbnail,
            }]
          : [];
      if (!nested.length) {
        toast.error("Add at least one subcategory.");
        return;
      }
      let created = 0;
      try {
        for (let index = 0; index < nested.length; index += 1) {
          const item = nested[index];
          onProgress?.({
            id: item.id,
            name: item.name.trim(),
            index: index + 1,
            total: nested.length,
          });
          await createSubcategory({
            name: item.name.trim(),
            categoryId: values.parentId,
            active: item.active,
            featured: item.featured,
            cover: item.cover,
            thumbnail: item.thumbnail,
          });
          created += 1;
        }
      } catch (error) {
        if (created > 0) onSave();
        const reason = errorMessage(error, "Failed to create subcategory");
        toast.error(
          created > 0
            ? `Created ${created} ${created === 1 ? "subcategory" : "subcategories"}. ${reason}`
            : reason
        );
        console.error("Error submitting form:", error);
        return;
      }
      onSave();
      onClose();
      toast.success(
        nested.length === 1 ? "Subcategory created successfully!" : "Subcategories created successfully!"
      );
      return;
    }

    const nested = (values.subcategories || []).filter((item) => item.name.trim());
    const steps = 1 + nested.length;
    onProgress?.({
      name: values.name.trim(),
      index: 1,
      total: steps,
      kind: "category",
    });
    const categoryId = await createCategory({
      name: values.name.trim(),
      active: values.active,
      featured: values.featured,
      cover: values.cover,
      thumbnail: values.thumbnail,
    });

    if (nested.length && !categoryId) {
      toast.success("Category created. Subcategories were not linked because the server did not return a category id.");
      onSave();
      onClose();
      return;
    }

    for (let index = 0; index < nested.length; index += 1) {
      const item = nested[index];
      onProgress?.({
        id: item.id,
        name: item.name.trim(),
        index: index + 2,
        total: steps,
      });
      await createSubcategory({
        name: item.name.trim(),
        categoryId,
        active: item.active,
        featured: item.featured,
        cover: item.cover,
        thumbnail: item.thumbnail,
      });
    }

    onSave();
    onClose();
    toast.success("Category created successfully!");
  } catch (error) {
    toast.error(errorMessage(error, "Failed to create category"));
    console.error("Error submitting form:", error);
  }
};

const updateCategory = async (values) => {
  const slug = values.slug;
  const hasImage = values.cover instanceof File || values.thumbnail instanceof File;
  const text = {
    name: values.name,
    type: "Department",
    ...flags(values),
  };

  const response = hasImage
    ? await axios.post(
        `${apiRoot}/admin/categories/${slug}/update`,
        (() => {
          const form = new FormData();
          form.append("name", text.name);
          form.append("type", text.type);
          form.append("is_active", String(text.is_active));
          form.append("featured", String(text.featured));
          appendImageFile(form, "cover_image", values.cover);
          appendImageFile(form, "thumbnail", values.thumbnail);
          return form;
        })(),
        { headers: authHeaders() }
      )
    : await axios.put(`${apiRoot}/admin/categories/${slug}`, text, {
        headers: { ...authHeaders(), "Content-Type": "application/json" },
      });

  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["000", "001"])) {
    throw new Error(envelope?.reason || "Failed to update category");
  }
};

const updateSubcategory = async (values) => {
  const hasImage = values.cover instanceof File || values.thumbnail instanceof File;
  const text = {
    name: values.name,
    category_id: Number(values.categoryId),
    ...flags(values),
  };

  const response = hasImage
    ? await axios.post(
        `${apiRoot}/admin/subcategories/${values.id}/update`,
        (() => {
          const form = new FormData();
          form.append("name", text.name);
          form.append("category_id", String(text.category_id));
          form.append("is_active", String(text.is_active));
          form.append("featured", String(text.featured));
          appendImageFile(form, "cover_image", values.cover);
          appendImageFile(form, "thumbnail", values.thumbnail);
          return form;
        })(),
        { headers: authHeaders() }
      )
    : await axios.put(`${apiRoot}/admin/subcategories/${values.id}`, text, {
        headers: { ...authHeaders(), "Content-Type": "application/json" },
      });

  const envelope = readEnvelope(response);
  if (!isSuccess(envelope, ["000", "001"])) {
    throw new Error(envelope?.reason || "Failed to update subcategory");
  }
};

export const handleCategoryUpdate = async (values, { onSave, onClose }) => {
  if (!localStorage.getItem("auth_token")) {
    toast.error("Failed to update category");
    return;
  }

  try {
    if (values.record === "subcategory") {
      if (!values.id || !values.categoryId) {
        toast.error("Failed to update subcategory");
        return;
      }
      await updateSubcategory(values);
      onSave();
      onClose();
      toast.success("Subcategory updated successfully!");
      return;
    }

    if (!values.slug) {
      toast.error("Failed to update category");
      return;
    }
    await updateCategory(values);
    onSave();
    onClose();
    toast.success("Category updated successfully!");
  } catch (error) {
    toast.error(errorMessage(error, "Failed to update category"));
    console.error("Error updating category:", error);
  }
};

export const deleteCategory = async (category, { setSubmitting, onDelete, onClose }) => {
  try {
    const subcategory = isSubcategoryRecord(category);
    const url = subcategory
      ? `${apiRoot}/admin/subcategories/${category?.id}`
      : `${apiRoot}/admin/categories/${category?.category_slug || category?.categorySlug}`;

    const response = await axios.delete(url, { headers: authHeaders() });
    const envelope = readEnvelope(response);
    if (!isSuccess(envelope, ["000"])) {
      throw new Error(envelope?.reason || "Failed to delete category");
    }
    onDelete();
    onClose();
    toast.success(subcategory ? "Subcategory deleted successfully!" : "Category deleted successfully!");
  } catch (error) {
    toast.error(errorMessage(error, "Failed to delete category"));
    console.error("Error deleting category:", error);
  } finally {
    setSubmitting?.(false);
  }
};
